import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmDialogComponent } from 'app/layout/components/confirm-dialog/confirm-dialog.component';
import { ToastService } from '@core/components/toasts/toasts.service';
import { Observable, Subject } from 'rxjs';
import { DomainService } from 'services/domain.service';
import { debounceTime, switchMap, tap } from 'rxjs/operators';
import { ExcusedAbsenceModel } from 'services/models/corporate/excusedAbsence.model';
import { comparer, customPattern, customPatternNumbers, guidEmpty, omit_special_char } from 'utils/helps';
import { GeographyService } from 'services/geography.service';
import { ExcusedAbsenceService } from 'services/corporate/excusedAbsence.service';
import { FormAbstact } from 'app/views/pages/abstract/form-abstract';
import { ActivatedRoute, Router } from '@angular/router';
import { EmployeeModel } from 'services/models/employee.model';
import { EmployeeService } from 'services/corporate/employee.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import moment from 'moment-business-days';
import { CustomValidators } from 'ng2-validation';
import { QueryParams } from 'services/models/odata/queryParams';
import { FormValidations } from 'utils/form-validation';
import { OptionModel } from 'services/models/model';
import { ReproveAbsenceDialogComponent } from '../reprove-absence-dialog/reprove-absence-dialog.component';
import { ExcusedAbsenceQualificationType } from 'services/enums/ExcusedAbsenceQualificationType.enum';
import { TimeBonusReasonModel } from 'services/models/corporate/timeBonusReason.model';
import TimeOccurrenceParameterEnum from 'services/enums/timeOccurrenceParameters.enum';
import { FileService } from '@core/services/file.service';

@Component({
  selector: 'app-qualify-excused-absence',
  templateUrl: './qualify-excused-absence.component.html',
  styleUrls: ['./qualify-excused-absence.component.scss'],
})
export class QualifyExcusedAbsenceModalComponent 
extends FormAbstact<ExcusedAbsenceModel, ExcusedAbsenceService>
implements OnInit {
  @ViewChild('documentPreviewImage') documentPreviewImage!: ElementRef;

  excusedAbsenceId: string;
  companyId: string;
  excusedAbsence: ExcusedAbsenceModel = new ExcusedAbsenceModel();
  employee: EmployeeModel = new EmployeeModel();
  onlyView: boolean = false;
  documentOnlyView: boolean = false;
  isSequential: boolean = false;
  isLoading = false;
  stateOptions: OptionModel[] = [];
  absenceTypes: any[] = [];
  workLeaveTypes: any[] = [];
  icdClassifications: any[] = [];
  qualifiedAbsencesOptions: any[] = [];
  pattern = customPattern;
  numberPattern = customPatternNumbers;
  omitChar = omit_special_char;
  compareValues = comparer;
  icdClassificationInput = new Subject<string>();
  icdClassificationDescription: string = null;
  documentPreviewUrl: SafeResourceUrl | null = null;
  mimeType: string | null = null;
  private documentBlob: Blob | null = null;
  rotationAngle = 0;
  readonly minImageZoom = 1;
  readonly maxImageZoom = 4;
  imageZoom = this.minImageZoom;
  isPanning = false;
  private readonly imageZoomStep = 0.5;
  private panX = 0;
  private panY = 0;
  private panStartX = 0;
  private panStartY = 0;
  private panOriginX = 0;
  private panOriginY = 0;
  hasAttachedDocument = true;
  usesPreAbono = false;
  hasHourControlExcusedAbsence = false;
  showQualificationTypeSelection = false;
  isPreAbonoMode = false;
  isInitializing = true;
  bonusReasonOptions: TimeBonusReasonModel[] = [];
  linkedToPreTreatment = false;
  
  constructor(
    private formBuilder: UntypedFormBuilder,
    private activeRoute: ActivatedRoute,
    private router: Router,
    private _modalService: NgbModal,
    public activeModal: NgbActiveModal,
    private service: ExcusedAbsenceService,
    private _domainService: DomainService,
    private geographyService: GeographyService,
    private _employeeService: EmployeeService,
    private _sanitizer: DomSanitizer,
    private _toastr: ToastService,
    private _fileService: FileService,
  ) {
    super(service, activeRoute, router, _toastr);
  }

  ngOnInit(): void {
    if (this.documentOnlyView) {
      this.loadDocumentOnlyPreview();
      return;
    }

    this.usesPreAbono = this._domainService.timeOccurrenceParameters.hasEnabled(
      TimeOccurrenceParameterEnum.EnablePreAbono
    );
    this.hasHourControlExcusedAbsence = this._domainService.timeOccurrenceParameters.hasEnabled(
      TimeOccurrenceParameterEnum.HasHourControlExcusedAbsence
    );
    this.initializeForm();

    this.loadUf().pipe(
      tap(),
      switchMap(() => this.loadicdClassifications()),
      switchMap(() => this.loadWorkLeaveTypes()),
      switchMap(() => this.loadAbsenceTypes())
    ).subscribe({
      next: () => {
        this.getAbsenceAndSetValues();
      },
      error: (err) => {
        console.error('Erro ao carregar dados iniciais', err);
        this.isInitializing = false;
        this._toastr.showError('Não foi possível carregar os dados iniciais.');
      }
    });
  }

  private loadDocumentOnlyPreview(): void {
    this.service.get(this.excusedAbsenceId).subscribe(
      (res) => {
        this.excusedAbsence = res.data;
        if (!this.excusedAbsence?.fileKey || !this.excusedAbsence?.fileName) {
          this.hasAttachedDocument = false;
          this.documentPreviewUrl = null;
          this.isInitializing = false;
          this._toastr.showWarning('Esta ausência não possui documento anexado.');
          return;
        }
        this.hasAttachedDocument = true;
        this.loadDocumentPreview();
        this.isInitializing = false;
      },
      () => {
        this.isInitializing = false;
        this._toastr.showError('Não foi possível carregar o documento da ausência.');
      }
    );
  }

  ngAfterViewInit(): void {
    this.loadIcdClassificationsLike();
  }

  initializeForm() {
    this.form = new UntypedFormGroup({
      dateBegin: new UntypedFormControl('', [Validators.required]),
      dateEnd: new UntypedFormControl(''),
      amountDays: new UntypedFormControl(0, [Validators.required, Validators.min(1)]),
      workLeaveTypeId: new UntypedFormControl(this.idGuid, [Validators.required, FormValidations.guidRequired]),
      absenceTypeId: new UntypedFormControl(this.idGuid, [Validators.required, FormValidations.guidRequired]),
      icdClassificationId: new UntypedFormControl(null),
      issuerName: new UntypedFormControl(''),
      issuerUF: new UntypedFormControl(''),
      issuerCRM: new UntypedFormControl(''),
      issuerNumber: new UntypedFormControl(''),
      fileKey: new UntypedFormControl(''),
      fileName: new UntypedFormControl(''),
      sequentialReference: new UntypedFormControl(null),
      bonusReasonId: new UntypedFormControl(null),
      hourBegin: new UntypedFormControl(null),
      hourEnd: new UntypedFormControl(null),
    });

    if (this.onlyView) this.form.disable();
  }

  selectQualificationType(type: 'LEGAL' | 'PRE_ABONO'): void {
    this.isPreAbonoMode = type === 'PRE_ABONO';
    this.showQualificationTypeSelection = false;
    this.configureFormForQualificationType();

    if (this.isPreAbonoMode) {
      this.loadPreAbonoContext();
      return;
    }

    if (this.onlyView) this.form.disable();
  }

  private configureFormForQualificationType(): void {
    const legalControls = [
      'workLeaveTypeId',
      'absenceTypeId',
      'icdClassificationId',
      'issuerName',
      'issuerCRM',
      'issuerNumber',
      'issuerUF',
      'sequentialReference',
    ];

    if (this.isPreAbonoMode) {
      legalControls.forEach((controlName) => {
        this.form.get(controlName)?.clearValidators();
        this.form.get(controlName)?.updateValueAndValidity();
      });
      this.form.get('bonusReasonId')?.setValidators([Validators.required, FormValidations.guidRequired]);
      this.form.get('dateBegin')?.setValidators([Validators.required]);
      this.form.get('dateEnd')?.setValidators([Validators.required]);
      this.form.get('amountDays')?.setValidators([Validators.required, Validators.min(1)]);
      this.form.updateValueAndValidity();
      return;
    }

    this.form.get('bonusReasonId')?.clearValidators();
    this.form.get('bonusReasonId')?.updateValueAndValidity();
    this.form.get('workLeaveTypeId')?.setValidators([
      Validators.required,
      FormValidations.guidRequired,
    ]);
    this.form.get('absenceTypeId')?.setValidators([
      Validators.required,
      FormValidations.guidRequired,
    ]);
    this.form.get('workLeaveTypeId')?.updateValueAndValidity();
    this.form.get('absenceTypeId')?.updateValueAndValidity();
  }

  private loadPreAbonoContext(): void {
    this.isLoading = true;
    this.service.getQualificationContext(this.excusedAbsenceId).subscribe(
      (context) => {
        this.linkedToPreTreatment = !!context.timeOccurrenceId;
        this.bonusReasonOptions = context.allowedBonusReasons.sort((a, b) => a.integrationCode.localeCompare(b.integrationCode)) ?? [];
        const selectedBonusReasonId = context.selectedBonusReasonId ?? null;
        this.form.patchValue({ bonusReasonId: selectedBonusReasonId });
        this.isLoading = false;
        if (this.onlyView) this.form.disable();
      },
      () => {
        this.isLoading = false;
        this._toastr.showError('Não foi possível carregar os motivos de abono.');
      }
    );
  }

  getAbsenceAndSetValues() {
    this.service.get(this.excusedAbsenceId).subscribe(
      (res) => {
        this.excusedAbsence = res.data;
        this.excusedAbsence.formatDates();
        this.excusedAbsence.dateEnd = this.excusedAbsence.dateEnd == "0001-01-01" ? null : this.excusedAbsence.dateEnd;
        if (this.excusedAbsence.sequentialReference && this.excusedAbsence.sequentialReference > 0) {
          this.isSequential = true;
          this.loadQualifiedAbsences();
        }
        this.form.patchValue({
          ...this.excusedAbsence,
          hourBegin: this.toTimeInput(this.excusedAbsence.hourBegin),
          hourEnd: this.toTimeInput(this.excusedAbsence.hourEnd),
        });
        this.isPreAbonoMode = this.isPreAbonoQualification(this.excusedAbsence.qualificationType);
        this.showQualificationTypeSelection =
          this.usesPreAbono && !this.onlyView && this.excusedAbsence.status === 'PENDING' && !this.isPreAbonoMode;

        if (this.isPreAbonoMode) {
          this.configureFormForQualificationType();
          this.loadPreAbonoContext();
        }

        this.getEmployee(this.excusedAbsence.employeeId);
        this.setHiddenClassificationOption();
        this.loadDocumentPreview();
        this.isInitializing = false;
      },
      () => {
        this.isInitializing = false;
        this._toastr.showError('Não foi possível carregar a ausência.');
      }
    );
  }

  getEmployee(employeeId: string) {
    this._employeeService.get(employeeId).subscribe((res) => {
      this.employee = res.data;
    });
  }

  private loadDocumentPreview() {
    this.resetImageView();
    this.documentBlob = null;
    this.service.getContent(this.excusedAbsence.fileKey, this.excusedAbsence.fileName.toLowerCase())
      .subscribe((response: any) => {
			this.mimeType = response.headers?.get("content-type") || "application/octet-stream";
			this.documentBlob = new Blob([response.body], { type: this.mimeType });
			const blobUrl = URL.createObjectURL(this.documentBlob);

			this.documentPreviewUrl = this._sanitizer.bypassSecurityTrustResourceUrl(blobUrl);
    }, (error) => {
			console.error("Erro ao carregar o documento para visualização", error);

			this.documentPreviewUrl = null;
			this.documentBlob = null;
    });
	}

  downloadAttachedDocument(): void {
    if (!this.documentBlob || !this.excusedAbsence?.fileName) {
      this._toastr.showWarning('Documento não disponível para download.');
      return;
    }

    this._fileService.saveFile(this.documentBlob, this.excusedAbsence.fileName);
    this._toastr.showSuccess('Download do arquivo realizado com sucesso!', 'top-right');
  }

  isPdf(): boolean {
    return this.mimeType === 'application/pdf';
  }
  
  isImage(): boolean {
    return this.mimeType?.startsWith('image/');
  }

  onImageLoad(): void {
    this.applyImageTransform();
  }

  rotateImage(): void {
    this.rotationAngle = (this.rotationAngle + 90) % 360;
    this.panX = 0;
    this.panY = 0;
    this.applyImageTransform();
  }

  zoomIn(): void {
    this.setImageZoom(this.imageZoom + this.imageZoomStep);
  }

  zoomOut(): void {
    this.setImageZoom(this.imageZoom - this.imageZoomStep);
  }

  onPreviewWheel(event: WheelEvent): void {
    event.preventDefault();
    const direction = event.deltaY < 0 ? 1 : -1;
    this.setImageZoom(this.imageZoom + direction * this.imageZoomStep);
  }

  onPreviewPointerDown(event: PointerEvent): void {
    if (this.imageZoom <= this.minImageZoom || event.button !== 0) return;

    event.preventDefault();
    const target = event.currentTarget as HTMLElement;
    target.setPointerCapture(event.pointerId);
    this.isPanning = true;
    this.panStartX = event.clientX;
    this.panStartY = event.clientY;
    this.panOriginX = this.panX;
    this.panOriginY = this.panY;
  }

  onPreviewPointerMove(event: PointerEvent): void {
    if (!this.isPanning) return;

    this.panX = this.panOriginX + (event.clientX - this.panStartX);
    this.panY = this.panOriginY + (event.clientY - this.panStartY);
    this.applyImageTransform();
  }

  onPreviewPointerUp(): void {
    this.isPanning = false;
  }

  private setImageZoom(nextZoom: number): void {
    const zoom = this.clamp(nextZoom, this.minImageZoom, this.maxImageZoom);
    if (zoom === this.imageZoom) return;

    this.imageZoom = zoom;
    if (zoom === this.minImageZoom) {
      this.panX = 0;
      this.panY = 0;
    }

    this.applyImageTransform();
  }

  private resetImageView(): void {
    this.rotationAngle = 0;
    this.imageZoom = this.minImageZoom;
    this.panX = 0;
    this.panY = 0;
    this.isPanning = false;
  }

  private applyImageTransform(): void {
    if (!this.documentPreviewImage?.nativeElement) return;

    const imageElement = this.documentPreviewImage.nativeElement as HTMLImageElement;
    const container = imageElement.parentElement;
    if (!container) return;

    const angle = this.rotationAngle || 0;
    const isVertical = angle === 90 || angle === 270;
    const containerHeight = container.clientHeight;
    const containerWidth = container.clientWidth;
    const imageHeight = imageElement.clientHeight;
    const imageWidth = imageElement.clientWidth;
    if (!imageWidth || !imageHeight || !containerWidth || !containerHeight) return;

    const fitScale = isVertical
      ? Math.min(containerHeight / imageWidth, containerWidth / imageHeight, 1)
      : Math.min(containerWidth / imageWidth, containerHeight / imageHeight, 1);
    const scale = fitScale * this.imageZoom;

    const visualWidth = (isVertical ? imageHeight : imageWidth) * scale;
    const visualHeight = (isVertical ? imageWidth : imageHeight) * scale;
    const maxPanX = Math.max(0, (visualWidth - containerWidth) / 2);
    const maxPanY = Math.max(0, (visualHeight - containerHeight) / 2);
    this.panX = this.clamp(this.panX, -maxPanX, maxPanX);
    this.panY = this.clamp(this.panY, -maxPanY, maxPanY);

    imageElement.style.transform = `translate(${this.panX}px, ${this.panY}px) rotate(${angle}deg) scale(${scale})`;
    imageElement.style.transformOrigin = 'center center';
    imageElement.style.maxWidth = '100%';
    imageElement.style.maxHeight = '100%';
    imageElement.style.objectFit = 'contain';
    container.style.overflow = 'hidden';
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, Math.round(value * 100) / 100));
  }

  setHiddenClassificationOption() {
    const isValid = this.excusedAbsence.icdClassificationId && this.excusedAbsence.icdClassificationId !== guidEmpty;
    if (isValid) {
      const alreadyExists = this.icdClassifications.some(opt => opt.value === this.excusedAbsence.icdClassificationId);

      if (!alreadyExists) {
        this.icdClassifications.push({
          value: this.excusedAbsence.icdClassificationId,
          label: this.icdClassificationDescription,
        });
      }
    }
  }

  loadUf() {
    return this.geographyService.getStates().pipe(
      tap(ufs => {
        this.stateOptions = ufs.data.map((s) => ({
          label: s.name,
          value: s.acronym,
        }));
      })
    );
  }

  loadicdClassifications(): Observable<any> {
    return this._service.listICDClassifications().pipe(
      tap((res) => {
        this.icdClassifications = res.sort((a, b) => Number(a.code) - Number(b.code)).map((j) => j.toOption());
      })
    );
  }

  loadIcdClassificationsLike() {
    this.icdClassificationInput.pipe(debounceTime(1000)).subscribe((char) => {
      if (char === null) return;
  
      this.isLoading = true;
      this._service.listICDClassifications(null, char).subscribe((res) => {
        this.icdClassifications = res.sort((a, b) => Number(a.code) - Number(b.code)).map((j) => j.toOption());
        this.isLoading = false;
      });
    });
  }

  loadWorkLeaveTypes(): Observable<any> {
    return this._service.listWorkLeaveTypes().pipe(
      tap((res) => {
        this.workLeaveTypes = res.sort((a, b) => Number(a.code) - Number(b.code)).map((j) => j.toOption());
      })
    );
  }

  loadAbsenceTypes(): Observable<any> {
    return this._service.listTypesByCompany(this.companyId).pipe(
      tap((res) => {
        this.absenceTypes = res.sort((a, b) => Number(a.code) - Number(b.code)).map((j) => j.toOption());
      })
    );
  }

  loadQualifiedAbsences() {
    const params = {
          select: ["id", "sequential", "dateBegin"],
          orderByOrder: "asc",
          orderBy: "sequential",
        } as QueryParams;
    this.service.listAbsencesByEmployee(params, this.excusedAbsence.employeeId).subscribe((res) => {
      this.qualifiedAbsencesOptions = res.data
        .filter((x) => x.enable && x.sequential && x.sequential != 0)
        .map((x) => { 
          return { label: `${x.sequential} - Ausência que iniciou no dia: ${moment(x.dateBegin).format("DD/MM/YYYY")}`, value: x.sequential } 
        })
    });
  }
  
  setLoadSpinner(state: boolean) {
    this.isLoading = state;
  }

  onConfirm() {
    if (!this.validaForm() || !this.hoursAreValid()) return;
  
    const modalRef = this._modalService.open(ConfirmDialogComponent, {
      windowClass: 'confirm-dialog-modal',
      backdropClass: 'confirm-dialog-backdrop'
    });
    modalRef.componentInstance.title = this.isPreAbonoMode
      ? 'Integração de Pré-Abono'
      : 'Integração de Ausência Legal';
    modalRef.componentInstance.message = this.isPreAbonoMode
      ? 'Deseja enviar a qualificação de pré-abono via integração para o ERP?'
      : 'Deseja enviar a qualificação realizada via integração para o ERP?';
    modalRef.componentInstance.messageConfirm = "Salvar e integrar";
    modalRef.componentInstance.messageCancel = "Salvar e não integrar";
    modalRef.componentInstance.neutralStyle = true;
    modalRef.componentInstance.focusConfirm = true;
  
    modalRef.result
      .then((result: boolean) => {
        if (result === undefined) return;
        const model = this.object.updateModel(this.form?.value);
        model.integrate = result;
        model.id = this.excusedAbsenceId;
        model.employeeId = this.employee.id;
        model.companyId = this.employee.companyId;
        model.qualificationType = this.isPreAbonoMode
          ? ExcusedAbsenceQualificationType.PreAbono.id.toString()
          : ExcusedAbsenceQualificationType.LegalAbsence.id.toString();
        if (!model.amountDays) model.amountDays = 0;
        if (!this.hasPreAbonoHourRange()) {
          model.hourBegin = null;
          model.hourEnd = null;
        }
  
        this.isLoading = true;
        this.service.save(this.excusedAbsenceId, model).subscribe(() => {
          const message = result
            ? this.isPreAbonoMode
              ? 'Pré-abono qualificado e integrado com sucesso!'
              : 'Qualificação realizada e integrada com sucesso!'
            : this.isPreAbonoMode
              ? 'Pré-abono qualificado com sucesso!'
              : 'Qualificação realizada com sucesso!';
          this._toastr.showSuccess(message);
          this.activeModal.close(true);
        }, () => this.isLoading = false, () => this.isLoading = false);
      })
      .catch(() => {});
  }

  openReproveAbsence() {
    const modalRef = this._modalService.open(ReproveAbsenceDialogComponent, {
      windowClass: 'confirm-dialog-modal',
      backdropClass: 'confirm-dialog-backdrop'
    });

    modalRef.closed.subscribe((submit) => {
      if (submit === false) return;

      const reproveReason = modalRef.componentInstance.reproveReason.trim().length > 0
        ? modalRef.componentInstance.reproveReason
        : "Não informado";

      this.isLoading = true;
      this.service.reproveExcusedAbsence(this.excusedAbsenceId, reproveReason).subscribe(
        () => {
          this._toastr.showSuccess("Ausência legal reprovada com sucesso!");
          this.onDismiss();
          this.isLoading = false;
        },
        () => { this.isLoading = false; }
      );
    });
  }

  onDismiss(): void {
    this.activeModal.close(true);
  }

  getSelectedTooltip(options = [], id: string): string {
    const selectedOption = options.find(
      option => option.value === this.form.controls[id].value
    );
    return selectedOption ? selectedOption.label : 'Selecionar uma opção';
  }

  dateBeginChange() {
    const begin = this.dateBeginField.value;
    const days = parseInt(this.amountDaysField.value, 10);

    if (begin && this.dateEndField.value) {
      this.dateEndField.setValidators(CustomValidators.minDate(moment(begin).format("YYYY-MM-DD")));
    } else if (!begin) {
      this.dateEndField.clearValidators();
    }
    this.dateEndField.updateValueAndValidity();
  
    if (begin && days) {
      const end = moment(begin).add(days - 1, "days").format("YYYY-MM-DD");
      this.dateEndField.setValue(end);
      this.dateEndField.updateValueAndValidity();
    }
  }
  
  amountDaysChange() {
    const begin = this.dateBeginField.value;
    const days = parseInt(this.amountDaysField.value, 10);
  
    if (begin && days) {
      const end = moment(begin).add(days - 1, "days").format("YYYY-MM-DD");
      this.dateEndField.setValue(end);
      this.dateEndField.updateValueAndValidity();
    } else {
      this.dateEndField.setValue(null);
      this.dateEndField.updateValueAndValidity();
    }
  }
  
  dateEndChange() {
    const begin = this.dateBeginField.value;
    const end = this.dateEndField.value;

    if (end) {
      this.dateEndField.setValidators(CustomValidators.minDate(moment(begin).format("YYYY-MM-DD")));
    } else {
      this.dateEndField.clearValidators();
    }
    this.dateEndField.updateValueAndValidity();
  
    if (begin && end) {
      const startDate = moment(begin);
      const endDate = moment(end);
      const duration = endDate.diff(startDate, "days") + 1;
      this.amountDaysField.setValue(duration);
      this.amountDaysField.updateValueAndValidity();
    }
  }

  isSequentialChange(ev: any) {
    this.isSequential = ev.target.checked;
    if (!ev.target.checked) {
      this.form.get('sequentialReference').setValue(null);
      this.form.get('sequentialReference').clearValidators();
      this.form.get('sequentialReference').updateValueAndValidity();
      return;
    }

    this.form.get('sequentialReference').setValidators(Validators.required);
    this.form.get('sequentialReference').setValue('');
    this.form.updateValueAndValidity();
    this.loadQualifiedAbsences();
  }

  get dateBeginField() {
    return this.form.get("dateBegin") as UntypedFormControl;
  }
  get dateEndField() {
    return this.form.get("dateEnd") as UntypedFormControl;
  }
  get amountDaysField() {
    return this.form.get("amountDays") as UntypedFormControl;
  }

  get bonusReasonIdField() {
    return this.form.get("bonusReasonId") as UntypedFormControl;
  }

  getSelectedBonusReasonLabel(): string {
    const selected = this.bonusReasonOptions.find(
      (item) => item.id === this.bonusReasonIdField?.value
    );
    return selected?.title ?? 'Selecionar';
  }

  private get preAbonoHours(): { active: boolean; begin: string; end: string } {
    return {
      active: this.isPreAbonoMode && this.hasHourControlExcusedAbsence,
      begin: this.form.get('hourBegin')?.value,
      end: this.form.get('hourEnd')?.value,
    };
  }

  private hasPreAbonoHourRange(): boolean {
    const { active, begin, end } = this.preAbonoHours;
    return active && !!begin && !!end;
  }

  private hoursAreValid(): boolean {
    const { active, begin, end } = this.preAbonoHours;
    if (!active) return true;

    const hasBegin = !!begin;
    const hasEnd = !!end;

    if (hasBegin !== hasEnd) {
      this._toastr.showWarning('Informe a hora inicial e a hora final, ou deixe as duas em branco para o dia inteiro.');
      return false;
    }

    const sameDay = this.dateBeginField.value && this.dateEndField.value
      && this.dateBeginField.value === this.dateEndField.value;

    if (hasBegin && hasEnd && sameDay && begin >= end) {
      this._toastr.showWarning('A hora final deve ser maior que a hora inicial.');
      return false;
    }

    return true;
  }

  private toTimeInput(value: string): string | null {
    if (!value) return null;
    const match = /^(\d{2}:\d{2})/.exec(value);
    return match ? match[1] : value;
  }

  private isPreAbonoQualification(qualificationType: string | number): boolean {
    return qualificationType == ExcusedAbsenceQualificationType.PreAbono.id.toString()
      || qualificationType == ExcusedAbsenceQualificationType.PreAbono.value.toString();
  }
}
